/**
 * Register Action — Phase 7 (ADR 0003).
 *
 * Server-side validation uses the same `registerSchema` zod schema
 * the form imports, eliminating client/server validation drift.
 */

import { redirect, type ActionFunctionArgs } from 'react-router-dom';
import { registerUser } from '@/services/keycloakAuthService';
import { registerSchema } from '@/modules/auth/schemas';
import { getFormDataObject, actionError, transformApiError } from './helpers';

export async function registerAction({ request }: ActionFunctionArgs) {
  try {
    const data = await getFormDataObject(request);

    const parsed = registerSchema.safeParse({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      phoneNumber: data.phoneNumber || '',
      accountType: data.accountType === 'organization' ? 'organization' : 'individual',
      organization: data.organization || '',
    });

    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const errors: Record<string, string> = {};
      Object.entries(flat).forEach(([key, msgs]) => {
        if (msgs && msgs.length > 0 && msgs[0]) errors[key] = msgs[0];
      });
      return actionError(errors, 400);
    }

    const { firstName, lastName, email, password, phoneNumber, accountType, organization } =
      parsed.data;

    await registerUser({
      email,
      password,
      firstName,
      lastName,
      phoneNumber: phoneNumber || '',
      isOrganization: accountType === 'organization',
      organizationName: accountType === 'organization' ? organization : undefined,
    });

    return redirect('/login?message=Registration successful! Please log in.');
  } catch (error) {
    console.error('Register action error:', error);

    const errorResponse = transformApiError(error);
    return actionError(errorResponse.errors ?? errorResponse.error ?? 'Registration failed', 400);
  }
}
